import { createSlice } from '@reduxjs/toolkit'
import { createProjectThunk } from '../projects/projects.thunks'
import { removeProject } from '../projects/projects.slice'
import {
  fetchProjectEditDataThunk,
  saveProjectRolePermissionsThunk,
  updateProjectMemberRoleThunk,
} from '../projectEdit/projectEdit.thunks'
import {
  createEmptyProjectMembersProjectState,
  createInitialProjectMembersState,
} from './projectMembers.state.utils'
import { fetchProjectMembersThunk, resolveFetchProjectMembersArg } from './projectMembers.thunks'

const ensureProject = (
  state: ReturnType<typeof createInitialProjectMembersState>,
  projectId: number,
) => {
  if (!state.byProjectId[projectId]) {
    state.byProjectId[projectId] = createEmptyProjectMembersProjectState()
  }
  return state.byProjectId[projectId]
}

const projectMembersSlice = createSlice({
  name: 'taskTrackerProjectMembers',
  initialState: createInitialProjectMembersState(),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(removeProject, (state, action) => {
        delete state.byProjectId[action.payload]
      })
      .addCase(createProjectThunk.fulfilled, (state, action) => {
        if (!action.payload) return

        const { id: projectId, members, roles } = action.payload
        const membersState = ensureProject(state, projectId)
        membersState.loading = false
        membersState.error = null
        membersState.members = members
        membersState.roles = roles
      })
      .addCase(fetchProjectMembersThunk.pending, (state, action) => {
        const { projectId } = resolveFetchProjectMembersArg(action.meta.arg)
        const membersState = ensureProject(state, projectId)
        membersState.loading = true
        membersState.error = null
      })
      .addCase(fetchProjectMembersThunk.fulfilled, (state, action) => {
        const membersState = ensureProject(state, action.payload.projectId)
        membersState.loading = false
        membersState.members = action.payload.members
        membersState.roles = action.payload.roles
      })
      .addCase(fetchProjectMembersThunk.rejected, (state, action) => {
        const payload = action.payload as { projectId: number; error: string }
        const membersState = ensureProject(state, payload.projectId)
        membersState.loading = false
        membersState.members = []
        membersState.roles = []
        membersState.error = payload.error
      })
      .addCase(fetchProjectEditDataThunk.fulfilled, (state, action) => {
        const membersState = ensureProject(state, action.payload.projectId)
        membersState.loading = false
        membersState.error = null
        membersState.members = action.payload.rawMembers
        membersState.roles = action.payload.roles
      })
      .addCase(saveProjectRolePermissionsThunk.fulfilled, (state, action) => {
        const membersState = state.byProjectId[action.payload.projectId]
        if (!membersState) return

        membersState.roles = membersState.roles.map((role) =>
          role.id === action.payload.role.id ? action.payload.role : role,
        )
        membersState.members = membersState.members.map((member) => {
          const memberRoleId = member.role?.id ?? member.roleId
          if (memberRoleId !== action.payload.role.id) return member

          return {
            ...member,
            role: action.payload.role,
          }
        })
      })
      .addCase(updateProjectMemberRoleThunk.fulfilled, (state, action) => {
        const membersState = state.byProjectId[action.payload.projectId]
        if (!membersState || !action.payload.role) return

        membersState.members = membersState.members.map((member) =>
          member.userId === action.payload.member.userId
            ? {
                ...member,
                roleId: action.payload.role!.id,
                role: action.payload.role,
              }
            : member,
        )
      })
  },
})

export default projectMembersSlice.reducer
